import { Link } from "react-router-dom";
import {
    PlayCircleIcon,
    SparkleIcon,
    LightningIcon,
    HeartIcon,
    TrendUpIcon,
    GlobeIcon,
    PaintBrushIcon,
    MusicNotesIcon,
    ShieldCheckIcon,
    ChartLineUpIcon,
    UsersThreeIcon,
    HashIcon,
    BellIcon,
    LockKeyIcon,
    ArrowRightIcon,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";

const GROUPS = [
    {
        title: "Watching reels",
        color: "#E01A4F",
        items: [
            { icon: PlayCircleIcon, title: "Vertical scroll-snap feed", body: "Buttery-smooth swipe-up reels that never buffer." },
            { icon: TrendUpIcon, title: "Personalised trending", body: "Algorithm trained on Indian regional tastes — not foreign data." },
            { icon: HashIcon, title: "Hashtag deep-dive", body: "Follow #bollywood, #cricket, #shayari and never miss a viral moment." },
            { icon: GlobeIcon, title: "13+ Indian languages", body: "Switch your feed language with one tap." },
        ],
    },
    {
        title: "Creating reels",
        color: "#FFA700",
        items: [
            { icon: PaintBrushIcon, title: "Pro-grade editor", body: "Multi-clip, speed control, filters, AR effects, text overlays." },
            { icon: MusicNotesIcon, title: "Indian music library", body: "Royalty-free desi beats, regional folk, Bollywood snippets." },
            { icon: SparkleIcon, title: "AI captions", body: "Auto-generate captions in your language. One-tap translate." },
            { icon: LightningIcon, title: "Upload in seconds", body: "Optimised for low-end Android. Even on 2G, 60-second reels upload fast." },
        ],
    },
    {
        title: "Growing as a creator",
        color: "#E01A4F",
        items: [
            { icon: ChartLineUpIcon, title: "Detailed analytics", body: "Views, demographics, retention, engagement — all in one dashboard." },
            { icon: HeartIcon, title: "Direct UPI payouts", body: "Creator Fund pays monthly in INR via UPI. No conversion fees." },
            { icon: UsersThreeIcon, title: "Collab tools", body: "Duets, stitches, group reels with built-in revenue split." },
            { icon: BellIcon, title: "Smart notifications", body: "Get notified only when it matters. Mute the noise." },
        ],
    },
    {
        title: "Safety & privacy",
        color: "#FFA700",
        items: [
            { icon: ShieldCheckIcon, title: "Content moderation", body: "24/7 human + AI review. Reports actioned in under 24 hours." },
            { icon: LockKeyIcon, title: "End-to-end DMs", body: "Direct messages between creators are end-to-end encrypted." },
            { icon: GlobeIcon, title: "India-hosted data", body: "All servers in India, owned by India. Full data sovereignty." },
            { icon: SparkleIcon, title: "Anti-bot defence", body: "Real human interactions only. Fake engagement is auto-filtered." },
        ],
    },
];

export default function Features() {
    return (
        <div className="px-5 md:px-10 max-w-6xl mx-auto py-16 md:py-24" data-testid="features-page">
            <Seo title="Features — India Reels" description="Discover every feature India Reels offers — feed, creator tools, monetisation, safety and more." path="/features" />
            <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                features
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl leading-tight">
                Every tool a creator <br />
                <span className="gradient-text">actually needs.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-3xl leading-relaxed">
                India Reels is more than a feed. It&apos;s a creator OS — built ground-up for Indian
                languages, devices, and culture.
            </p>

            <div className="mt-16 space-y-16">
                {GROUPS.map((g) => (
                    <section key={g.title}>
                        <div className="flex items-center gap-3 mb-8">
                            <div
                                className="w-2 h-10 rounded-full"
                                style={{ background: g.color }}
                            />
                            <h2 className="font-heading font-black text-2xl md:text-3xl">
                                {g.title}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            {g.items.map((it) => (
                                <div
                                    key={it.title}
                                    data-testid={`feature-${it.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="flex gap-4 p-5 rounded-3xl border border-white/10 bg-surface hover:border-white/20 transition-colors"
                                >
                                    <div
                                        className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                                        style={{ background: `${g.color}1F` }}
                                    >
                                        <it.icon size={24} weight="fill" color={g.color} />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-heading font-bold text-base mb-1">
                                            {it.title}
                                        </h3>
                                        <p className="text-text-secondary text-sm leading-relaxed">
                                            {it.body}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            <div className="mt-20 rounded-3xl bg-gradient-to-br from-primary/20 via-surface to-secondary/10 border border-white/10 p-10 md:p-14 text-center">
                <h2 className="font-heading font-black text-3xl md:text-4xl">
                    See it in action.
                </h2>
                <p className="text-white/70 mt-4 max-w-xl mx-auto">
                    The fastest way to feel the difference is to download the app.
                </p>
                <Link
                    to="/#download"
                    data-testid="features-download-cta"
                    className="btn-primary mt-8 px-7 py-4 inline-flex items-center gap-2"
                >
                    Download India Reels
                    <ArrowRightIcon size={18} weight="bold" />
                </Link>
            </div>
        </div>
    );
}
